'use client';

import { PromotionStar as PromotionStarIcon } from '@ahafood/icons';
import { Button } from '@ahafood/ui/components/button';
import { Skeleton } from '@ahafood/ui/components/skeleton';
import { assetPrefix } from '@ahm/api-wrappers-core';
import { cn } from '@ahm/common-helpers';
import { motion } from 'framer-motion';
import { useSearchParams, useRouter } from 'next/navigation';
import { useMemo } from 'react';
import type { Tag } from '@/api/ahafood/get-deal-fn';
import { processDealData } from '@/api/utils';
import ImageWithFallback from '@/app/[locale]/(main)/store/components/items/fallback-image';
import FreeModeSwiper from '@/app/[locale]/(main)/store/components/items/free-mode-swiper';
import { trackEvent } from '@/lib/event-tracking';
import { useIsMobile } from '@/lib/is-mobile/use-is-mobile';
import { useAiFoodOrderStore, useAuthStore } from '@/store';

const cardVariants = {
  offscreen: {
    opacity: 0,
    x: 50,
  },
  onscreen: {
    opacity: 1,
    x: 0,
    transition: {
      type: 'spring',
      bounce: 0.4,
      duration: 0.8,
    },
  },
};

type OldDealProps = {
  deals: Tag[];
  isLoading?: boolean;
  city_slug?: string;
  payload: {
    city_id: string;
    category_slug?: string;
    promotion?: boolean;
    freeship?: boolean;
    special_request?: string;
    hot_deal?: boolean;
    campaign_id?: string;
  };
};

export default function NewOldStoreDeal({
  deals,
  isLoading,
  payload,
  city_slug,
}: OldDealProps) {
  const isMobile = useIsMobile();
  const searchParams = useSearchParams();
  const router = useRouter();
  const { user } = useAuthStore();

  const cityId = useAiFoodOrderStore.use.city_id();
  const deal = payload.campaign_id
    ? parseInt(payload.campaign_id, 10)
    : parseFloat(searchParams.get('campaign_id')!);

  const processedDeals = useMemo(() => {
    if (!deals) return { deal: { tags: [], title: '' } };
    return processDealData(deals);
  }, [deals]);

  function handleDealToggle(d: number) {
    router.push(`/${city_slug}/danh-sach-cua-hang-co-campaign-${d}`);
    trackEvent('click_button', {
      button_name: 'super_deal_toggle',
      timestamp: Date.now(),
      deal_id: d,
      user_id: user?.cunstomerId,
    });
  }

  if (isLoading ?? !cityId) {
    return (
      <div className="flex flex-row flex-nowrap gap-4">
        {[...Array(isMobile ? 2 : 3)].map((_, index) => (
          <Skeleton
            key={`old-deal-skeleton-${index}`}
            className="h-[272px] w-48 flex-none rounded-xl bg-neutral-10/75 md:h-[320px] md:w-60"
          />
        ))}
      </div>
    );
  }

  if (!processedDeals.deal || processedDeals.deal.tags.length === 0)
    return null;

  return (
    <div>
      <h2 className="relative z-[5] mb-6 space-x-2 text-2xl font-bold drop-shadow-sm md:text-5xl">
        <span>🌟</span>
        <span>Super deal</span>
      </h2>
      <FreeModeSwiper
        navigation={false}
        className="w-full !overflow-visible"
        spaceBetween={16}
        pagination
      >
        {processedDeals.deal.tags.map((d, index) => (
          <FreeModeSwiper.Slide
            key={`deal-${d._id}-${index}`}
            style={{ width: isMobile ? 192 : 240, aspectRatio: '240/320' }}
          >
            <motion.div
              whileHover={{
                scale: 1.1,
                transition: {
                  type: 'spring',
                  stiffness: 300,
                  damping: 10,
                },
              }}
              whileTap={{ scale: 1.05 }}
              initial={isMobile ? { opacity: 1, x: 0 } : 'offscreen'}
              animate="onscreen"
              variants={cardVariants}
              transition={isMobile ? { delay: 0 } : { delay: index * 0.2 }}
            >
              <Button
                variant="ghost"
                onClick={() => {
                  handleDealToggle(d._id);
                }}
                className="group relative flex h-auto w-48 flex-col justify-start overflow-hidden rounded-xl bg-white p-0 transition-all hover:bg-white hover:shadow-8 md:w-60"
              >
                <div
                  className={cn(
                    'absolute z-[1] size-full rounded-xl bg-transparent',
                    {
                      'border-[5px] border-primary-50': d._id === deal,
                    }
                  )}
                ></div>
                <div className="relative aspect-square h-auto w-full bg-white">
                  <ImageWithFallback
                    src={
                      d.icon_image_url ??
                      `${assetPrefix}/static/images/empty-store.webp`
                    }
                    alt={d.description_vi_vn}
                    fill
                    style={{ objectFit: 'cover' }}
                    fallbackSrc={`${assetPrefix}/static/images/empty-store.webp`}
                  />
                </div>

                <div className="flex w-full flex-col flex-wrap items-center gap-2 px-4 pb-4 pt-3 text-xs font-medium capitalize text-neutral-50">
                  <h3 className="w-full truncate text-sm font-bold text-neutral-70 group-hover:line-clamp-2 group-hover:whitespace-pre-line group-hover:text-neutral-90 md:text-xl">
                    {d.title_vi_vn}
                  </h3>
                  <div className="flex w-full flex-nowrap items-center justify-center truncate">
                    <PromotionStarIcon className="mr-1 size-6" />
                    <span className="truncate text-xs font-medium md:text-sm">
                      {d.description_vi_vn}
                    </span>
                  </div>
                </div>
              </Button>
            </motion.div>
          </FreeModeSwiper.Slide>
        ))}
      </FreeModeSwiper>
    </div>
  );
}
